import type { ExceptionCategory } from './ExceptionAnalysis.api';
import type { GridColDef } from '@mui/x-data-grid';

// MUI DataGrid v7 signature: valueGetter/valueFormatter receive the raw
// value as the FIRST arg (not a `params` object as in v6). Calling
// `params.value` throws on internal sort/type-coercion passes where MUI
// invokes the getter with undefined-shaped args.

// Helper: join text[] columns to comma-separated string for both display
// and search. Applied via valueGetter so filtering/quick-filter/sorting
// operate on the joined string.
const arrayValueGetter = (value: any): string =>
  Array.isArray(value) ? value.join(', ') : '';

// Truncate long formulas at ~200 chars; hover shows full via native tooltip.
const formulaValueFormatter = (value: any): string => {
  if (!value) return '';
  const s = String(value);
  return s.length > 200 ? s.slice(0, 200) + '…' : s;
};

export interface CategoryMeta {
  slug: ExceptionCategory;
  label: string;
  detects: string;
  columns: GridColDef[];
}

// Column defs per category — fields match what the 6 DB views in app_public.*
// return. tenant + environment intentionally omitted from the grid (filtered
// server-side via the middleware, redundant to display).
export const CATEGORY_META: CategoryMeta[] = [
  {
    slug: 'metric-gaps',
    label: 'Metric Gaps',
    detects: 'View references a metric that no linked pivot defines',
    columns: [
      { field: 'view_name',      headerName: 'View',            flex: 2, minWidth: 220 },
      { field: 'missing_metric', headerName: 'Missing Metric',  flex: 1, minWidth: 180 },
      { field: 'item_type',      headerName: 'Type',            width: 140 },
      { field: 'linked_pivots',  headerName: 'Linked Pivots',   flex: 2, minWidth: 220,
        valueGetter: arrayValueGetter, sortable: false },
      { field: 'nav_paths',      headerName: 'Nav Paths',       flex: 3, minWidth: 300,
        valueGetter: arrayValueGetter, sortable: false },
    ],
  },
  {
    slug: 'attribute-gaps',
    label: 'Attribute Gaps',
    detects: 'View references an attribute/level/member that no linked model declares',
    columns: [
      { field: 'view_name',         headerName: 'View',                flex: 2, minWidth: 240 },
      { field: 'missing_attribute', headerName: 'Missing Attribute',   flex: 2, minWidth: 220 },
      { field: 'item_type',         headerName: 'Type',                width: 140 },
      { field: 'linked_models',     headerName: 'Linked Models',       flex: 2, minWidth: 220,
        valueGetter: arrayValueGetter, sortable: false },
      { field: 'nav_paths',         headerName: 'Nav Paths',           flex: 3, minWidth: 300,
        valueGetter: arrayValueGetter, sortable: false },
    ],
  },
  {
    slug: 'view-vs-pivot-formula-conflicts',
    label: 'View ↔ Pivot Formula',
    detects: 'View has its own formula for a metric AND a linked pivot defines it differently (runtime resolution race)',
    columns: [
      { field: 'view_name',     headerName: 'View',           flex: 2, minWidth: 220 },
      { field: 'data_index',    headerName: 'Data Index',     flex: 1, minWidth: 160 },
      { field: 'view_formula',  headerName: 'View Formula',   flex: 3, minWidth: 260,
        valueFormatter: formulaValueFormatter },
      { field: 'pivot_name',    headerName: 'Pivot',          flex: 1, minWidth: 180 },
      { field: 'pivot_formula', headerName: 'Pivot Formula',  flex: 3, minWidth: 260,
        valueFormatter: formulaValueFormatter },
      { field: 'nav_paths',     headerName: 'Nav Paths',      flex: 2, minWidth: 260,
        valueGetter: arrayValueGetter, sortable: false },
    ],
  },
  {
    slug: 'pivot-formula-conflicts',
    label: 'Pivot Formula',
    detects: 'Same metric computed with different formulas across pivots',
    columns: [
      { field: 'data_index',          headerName: 'Data Index',       flex: 1, minWidth: 180 },
      { field: 'pivot_count',         headerName: 'Pivots',           width: 100, type: 'number' },
      { field: 'n_distinct_formulas', headerName: 'Distinct Formulas',width: 160, type: 'number' },
      { field: 'pivots',              headerName: 'Pivot Names',      flex: 3, minWidth: 300,
        valueGetter: arrayValueGetter, sortable: false },
      { field: 'formulas',            headerName: 'Formulas',         flex: 3, minWidth: 300,
        valueGetter: arrayValueGetter, sortable: false },
    ],
  },
  {
    slug: 'renderer-conflicts',
    label: 'Renderer',
    detects: 'Same data_index rendered with different formatters across views',
    columns: [
      { field: 'data_index',           headerName: 'Data Index',       flex: 1, minWidth: 220 },
      { field: 'view_count',           headerName: 'Views',            width: 100, type: 'number' },
      { field: 'n_distinct_renderers', headerName: 'Distinct Renderers', width: 180, type: 'number' },
      { field: 'renderers',            headerName: 'Renderer Names',   flex: 2, minWidth: 220,
        valueGetter: arrayValueGetter, sortable: false },
      { field: 'views',                headerName: 'Views',            flex: 3, minWidth: 300,
        valueGetter: arrayValueGetter, sortable: false },
      { field: 'nav_paths',            headerName: 'Nav Paths',        flex: 3, minWidth: 300,
        valueGetter: arrayValueGetter, sortable: false },
    ],
  },
  {
    slug: 'label-conflicts',
    label: 'Label',
    detects: 'Same data_index shown with different labels across views',
    columns: [
      { field: 'data_index',         headerName: 'Data Index',    flex: 1, minWidth: 220 },
      { field: 'view_count',         headerName: 'Views',         width: 100, type: 'number' },
      { field: 'n_distinct_labels',  headerName: 'Distinct Labels', width: 160, type: 'number' },
      { field: 'labels',             headerName: 'Label Values',  flex: 2, minWidth: 240,
        valueGetter: arrayValueGetter, sortable: false },
      { field: 'views',              headerName: 'Views',         flex: 3, minWidth: 300,
        valueGetter: arrayValueGetter, sortable: false },
      { field: 'nav_paths',          headerName: 'Nav Paths',     flex: 3, minWidth: 300,
        valueGetter: arrayValueGetter, sortable: false },
    ],
  },
];

export const getCategoryMeta = (slug: ExceptionCategory): CategoryMeta | undefined =>
  CATEGORY_META.find(c => c.slug === slug);
